import { createTheme, type ThemeOptions } from '@mui/material/styles';

/* Source: corepilot-handover-build/prototypes/design-system.md §2 (tokens) + §5 (MUI mapping).
 * Hex values mirror tokens.css; both files must move together. */

declare module '@mui/material/styles' {
  interface Palette {
    surface: {
      soft: string;
      soft2: string;
      iconTile: string;
      activeNav: string;
      draft: string;
    };
    border: {
      app: string;
      card: string;
      card2: string;
      soft: string;
      pill: string;
    };
  }
  interface PaletteOptions {
    surface?: Partial<Palette['surface']>;
    border?: Partial<Palette['border']>;
  }
}

const FONT_FAMILY = "'Inter', system-ui, -apple-system, sans-serif";
const FONT_FAMILY_TIGHT = "'Inter Tight', 'Inter', system-ui, sans-serif";

const typography: ThemeOptions['typography'] = {
  fontFamily: FONT_FAMILY,
  // Hero greeting (spec §2 typography: 48/48, -1.2px).
  h1: { fontSize: 48, lineHeight: '48px', fontWeight: 700, letterSpacing: '-1.2px' },
  h2: { fontSize: 28, lineHeight: '36px', fontWeight: 700, letterSpacing: '-0.4px' },
  // Card title (spec §2: 16/24).
  h3: { fontSize: 16, lineHeight: '24px', fontWeight: 600 },
  h4: { fontSize: 14, lineHeight: '20px', fontWeight: 600 },
  // Hero lede (spec §2: 18/28).
  subtitle1: { fontSize: 18, lineHeight: '28px', fontWeight: 400 },
  subtitle2: { fontSize: 14, lineHeight: '20px', fontWeight: 500 },
  // Body / sm (spec §2: 14/22.75).
  body1: { fontSize: 14, lineHeight: '22.75px', fontWeight: 400 },
  // Body / sm — UI (spec §2: 14/20).
  body2: { fontSize: 14, lineHeight: '20px', fontWeight: 400 },
  // Buttons (spec §2: 14/20 sm-UI).
  button: { fontSize: 14, lineHeight: '20px', fontWeight: 600, textTransform: 'none' },
  // Caption (spec §2: 12/16).
  caption: { fontSize: 12, lineHeight: '16px', fontWeight: 400 },
  // DRAFT, SECURE pills (spec §2: 12/16, 600, +0.3px, no transform).
  overline: { fontSize: 12, lineHeight: '16px', fontWeight: 600, letterSpacing: '0.3px', textTransform: 'none' },
};

const shape: ThemeOptions['shape'] = { borderRadius: 10 };

function buildComponents(palette: { primary: string; borderCard: string; bgMuted: string; borderInput: string; tooltipBg: string }): ThemeOptions['components'] {
  return {
    MuiCssBaseline: {
      styleOverrides: { body: { fontFamily: FONT_FAMILY } },
    },
    // Spec §5: elevation 0, border 1px palette.border.card, radius 16. Padding is per-card.
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          borderRadius: 16,
          border: `1px solid ${palette.borderCard}`,
          boxShadow: 'none',
        },
      },
    },
    // Spec §5: drop default boxShadow; outlined uses border.card; contained uses primary.
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { borderRadius: 8, padding: '8px 20px' },
        outlined: { borderColor: palette.borderCard },
        contained: { boxShadow: 'none', '&:hover': { boxShadow: 'none' } },
      },
    },
    MuiTextField: {
      defaultProps: { variant: 'outlined', size: 'small' },
    },
    // Spec §5: bg grey[50] (#f9fafb), border grey[200] (#e5e7eb), focus border primary + 3px outer ring rgba(255,130,67,.12).
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          backgroundColor: palette.bgMuted,
          '& fieldset': { borderColor: palette.borderInput },
          '&:hover fieldset': { borderColor: palette.borderInput },
          '&.Mui-focused fieldset': {
            borderColor: palette.primary,
            boxShadow: `0 0 0 3px rgba(255, 130, 67, 0.12)`,
          },
        },
      },
    },
    // Spec §5: bg grey[800] (#1f2937), no arrow, 4px radius, 12px medium.
    MuiTooltip: {
      defaultProps: { arrow: false },
      styleOverrides: {
        tooltip: {
          backgroundColor: palette.tooltipBg,
          color: '#ffffff',
          fontSize: 12,
          fontWeight: 500,
          lineHeight: '16px',
          padding: '6px 10px',
          borderRadius: 4,
          boxShadow: '0 10px 15px -3px rgba(0,0,0,.1), 0 4px 6px -4px rgba(0,0,0,.1)',
        },
      },
    },
  };
}

// Spec §5 palette.ts.
const lightPaletteOptions = {
  mode: 'light' as const,
  primary: { main: '#ff8243', dark: '#f36b3d', light: '#ffd6c2', contrastText: '#ffffff' },
  secondary: { main: '#0a192f', dark: '#0a192f', light: '#4b5563', contrastText: '#ffffff' },
  text: { primary: '#0a192f', secondary: '#4b5563', disabled: '#9ca3af' },
  background: { default: '#f8f7f4', paper: '#ffffff' },
  divider: '#f1ece8',
  error: { main: '#ef4444' },
  warning: { main: '#f59e0b' },
  success: { main: '#22c55e' },
  info: { main: '#3b82f6' },
  grey: {
    50: '#f9fafb',
    100: '#f3f4f6',
    200: '#e5e7eb',
    300: '#d1d5db',
    400: '#9ca3af',
    500: '#6b7280',
    600: '#4b5563',
    700: '#374151',
    800: '#1f2937',
    900: '#0a192f',
  },
  surface: {
    soft: '#faf4f1',
    soft2: '#f3eeea',
    iconTile: '#fff7ed',
    activeNav: '#fff5f2',
    draft: '#f3f4f6',
  },
  border: {
    app: '#f1ece8',
    card: '#ececec',
    card2: '#f3f4f6',
    soft: '#eae4dd',
    pill: '#ffedd5',
  },
};

const darkPaletteOptions = {
  mode: 'dark' as const,
  primary: { main: '#ff9257', dark: '#ff8243', light: '#ffab7a', contrastText: '#1a1a2e' },
  secondary: { main: '#e2e8f0', dark: '#cbd5e1', light: '#f8fafc', contrastText: '#0a192f' },
  text: { primary: '#ededf0', secondary: '#c4c4d0', disabled: '#5a5a6e' },
  background: { default: '#111118', paper: '#1a1a24' },
  divider: '#24242f',
  error: { main: '#ef4444' },
  warning: { main: '#f59e0b' },
  success: { main: '#22c55e' },
  info: { main: '#3b82f6' },
  grey: {
    50: '#22222e',
    100: '#1e1e28',
    200: '#334155',
    300: '#475569',
    400: '#64748b',
    500: '#8e8ea0',
    600: '#c4c4d0',
    700: '#d4d4e0',
    800: '#1f2937',
    900: '#ededf0',
  },
  surface: {
    soft: '#1f1f2c',
    soft2: '#14141c',
    iconTile: '#2a201a',
    activeNav: '#2a1f1c',
    draft: '#1e1e28',
  },
  border: {
    app: '#24242f',
    card: '#2a2a38',
    card2: '#22222e',
    soft: '#38384a',
    pill: '#3a2a1f',
  },
};

export const lightTheme = createTheme({
  palette: lightPaletteOptions,
  typography,
  shape,
  components: buildComponents({
    primary: lightPaletteOptions.primary.main,
    borderCard: lightPaletteOptions.border.card,
    bgMuted: lightPaletteOptions.grey[50],
    borderInput: lightPaletteOptions.grey[200],
    tooltipBg: lightPaletteOptions.grey[800],
  }),
});

export const darkTheme = createTheme({
  palette: darkPaletteOptions,
  typography,
  shape,
  components: buildComponents({
    primary: darkPaletteOptions.primary.main,
    borderCard: darkPaletteOptions.border.card,
    bgMuted: darkPaletteOptions.grey[50],
    borderInput: darkPaletteOptions.grey[200],
    tooltipBg: '#0a0a12',
  }),
});

export { FONT_FAMILY, FONT_FAMILY_TIGHT };
